import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import StudioPanel from '@/pages/StudioPanel';
import EnvironmentPanel from '@/pages/EnvironmentPanel';
import Lightbox from '@/components/Lightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  title: '',
  content: '',
  media_url: '',
  media_type: '',
  gallery: [],
  is_premium: false,
  episode_id: '',
  phase: '',
  concept: '',
  status: 'published',
  scheduled_at: '',
  publish_site: true,
  publish_to_social: false,
  social_platforms: []
};

const STATUS_LABELS = {
  draft: 'Brouillon',
  scheduled: 'Programmée',
  published: 'Publiée'
};

const DOW = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const TABS = ['console', 'studio', 'env'];

// Repli de navigation. L'URL reste la source première — c'est elle qui rend un
// écran partageable — mais elle se perd dans des cas ordinaires : bookmark sur
// /admin nu, lien reçu, page rouverte depuis l'écran d'accueil d'un mobile.
// Sans ce repli, on retombe alors sur Console en ayant l'impression d'avoir
// perdu sa place.
const LAST_TAB_KEY = 'voss.admin.lastTab';

const readLastTab = () => {
  try {
    const v = window.localStorage.getItem(LAST_TAB_KEY);
    return TABS.includes(v) ? v : 'console';
  } catch {
    // stockage refusé (navigation privée stricte) : on dégrade sans casser
    return 'console';
  }
};

const rememberLastTab = (v) => {
  try {
    window.localStorage.setItem(LAST_TAB_KEY, v);
  } catch { /* sans conséquence : l'URL reste la source première */ }
};

// Date d'affichage d'un post dans le planning
const postPlanDate = (p) => {
  if (p.status === 'scheduled') return p.scheduled_at;
  if (p.status === 'draft') return null; // non daté → strip « à planifier »
  return p.published_at || p.scheduled_at || p.created_at;
};

const dayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// 42 cellules (6 semaines), semaine commençant le lundi
const buildCalendarCells = (month) => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(month.getFullYear(), month.getMonth(), 1 - offset + i);
    cells.push(d);
  }
  return cells;
};

const AdminPage = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [calMonth, setCalMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [newGalleryUrl, setNewGalleryUrl] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // L'URL reste la source première (elle rend le lien partageable), mais elle
  // peut se perdre : bookmark sur /admin sans paramètres, lien externe, page
  // rouverte depuis l'écran d'accueil d'un mobile. Un repli local évite de
  // retomber sur Console dans ces cas-là.
  const tab = TABS.includes(tabParam) ? tabParam : readLastTab();
  const setTab = (next) => {
    rememberLastTab(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      return p;
    }, { replace: true });
  };

  // Si l'URL est arrivée sans onglet alors qu'on en avait un en mémoire, on la
  // remet en cohérence : sans ça l'écran et l'adresse se contrediraient, et le
  // rafraîchissement suivant repartirait de zéro.
  useEffect(() => {
    if (!TABS.includes(tabParam) && tab !== 'console') {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('tab', tab);
        return p;
      }, { replace: true });
    }
    // au montage seulement : on rattrape une URL incomplète
  }, []);
  const [lightbox, setLightbox] = useState(null);

  // Fenêtre d'observation (hero)
  const [hero, setHero] = useState(null);
  const [savingHero, setSavingHero] = useState(false);

  // Picker médiathèque — target: 'media' | 'gallery' | 'hero' | null
  const [pickerTarget, setPickerTarget] = useState(null);
  const [media, setMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaFolder, setMediaFolder] = useState('all');

  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image', 'video'],
      [{ 'align': [] }],
      [{ 'color': [] }, { 'background': [] }],
      ['blockquote', 'code-block'],
      ['clean']
    ]
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'link', 'image', 'video',
    'align', 'color', 'background', 'blockquote', 'code-block'
  ];

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchPosts();
    fetchHero();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/admin/posts`, {
        headers: authHeaders(),
        withCredentials: true
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const fetchHero = async () => {
    try {
      const response = await axios.get(`${API}/hero/settings`);
      setHero(response.data);
    } catch (error) {
      console.error('Error fetching hero settings:', error);
    }
  };

  const openPicker = async (target) => {
    setPickerTarget(target);
    setMediaFolder('all');
    if (media.length === 0) {
      setMediaLoading(true);
      try {
        const response = await axios.get(`${API}/admin/media`, {
          headers: authHeaders(),
          withCredentials: true
        });
        setMedia(response.data.files || []);
      } catch (error) {
        toast.error('Impossible de charger la médiathèque');
      } finally {
        setMediaLoading(false);
      }
    }
  };

  const pickMedia = (file) => {
    const isVideo = /\.mp4$/i.test(file.path);
    if (pickerTarget === 'media') {
      setFormData((prev) => ({ ...prev, media_url: file.url, media_type: isVideo ? 'video' : 'image' }));
    } else if (pickerTarget === 'gallery') {
      setFormData((prev) => ({ ...prev, gallery: [...prev.gallery, file.url] }));
    } else if (pickerTarget === 'hero') {
      setHero((prev) => ({ ...prev, media_url: file.url, media_type: isVideo ? 'video' : 'image' }));
    }
    // Galerie : on garde le picker ouvert pour un ajout multiple ; sinon on ferme
    if (pickerTarget !== 'gallery') setPickerTarget(null);
    else toast.success('Ajoutée à la galerie');
  };

  const mediaFolders = ['all', ...Array.from(new Set(media.map((f) => f.path.split('/')[0]))).sort()];
  const visibleMedia = mediaFolder === 'all' ? media : media.filter((f) => f.path.startsWith(mediaFolder + '/'));

  const handleSaveHero = async (e) => {
    e.preventDefault();
    setSavingHero(true);
    try {
      await axios.put(`${API}/admin/hero/settings`, hero, {
        headers: authHeaders(),
        withCredentials: true
      });
      toast.success('Fenêtre d\'observation mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde du hero');
    } finally {
      setSavingHero(false);
    }
  };

  const handleAddToGallery = () => {
    if (newGalleryUrl && newGalleryUrl.trim()) {
      setFormData(prev => ({
        ...prev,
        gallery: [...prev.gallery, newGalleryUrl.trim()]
      }));
      setNewGalleryUrl('');
    }
  };

  const handleRemoveFromGallery = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
  };

  const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const handleEdit = (post) => {
    setEditingId(post.id);
    setFormData({
      title: post.title || '',
      content: post.content || '',
      media_url: post.media_url || '',
      media_type: post.media_type || '',
      gallery: post.gallery || [],
      is_premium: !!post.is_premium,
      episode_id: post.episode_id || '',
      phase: post.phase || '',
      concept: post.concept || '',
      status: post.status || 'published',
      scheduled_at: toLocalInput(post.scheduled_at),
      publish_site: post.publish_site !== false,
      publish_to_social: false,
      social_platforms: post.social_platforms || []
    });
    document.getElementById('entry-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.status === 'scheduled' && !formData.scheduled_at) {
      toast.warning('Choisis une date/heure de publication');
      return;
    }
    const payload = {
      ...formData,
      scheduled_at: formData.scheduled_at ? new Date(formData.scheduled_at).toISOString() : null
    };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/posts/${editingId}`, payload, {
          headers: authHeaders(),
          withCredentials: true
        });
        toast.success('Entrée mise à jour');
        cancelEdit();
      } else {
        const response = await axios.post(`${API}/admin/posts`, formData, {
          headers: authHeaders(),
          withCredentials: true
        });

        if (response.data.social_media_results && response.data.social_media_results.length > 0) {
          const successful = response.data.social_media_results.filter(r => r.success);
          const failed = response.data.social_media_results.filter(r => !r.success);

          if (successful.length > 0) {
            toast.success(`Entrée consignée et publiée sur ${successful.map(r => r.platform).join(', ')}`);
          }
          if (failed.length > 0) {
            failed.forEach(r => {
              toast.warning(`Échec ${r.platform || 'réseau'}: ${r.error}`);
            });
          }
        } else {
          toast.success('Entrée consignée');
        }
        setFormData(EMPTY_FORM);
      }
      fetchPosts();
    } catch (error) {
      toast.error(editingId ? 'Erreur lors de la mise à jour' : 'Erreur lors de la création');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Supprimer cette entrée du Registre ?')) return;

    try {
      await axios.delete(`${API}/admin/posts/${postId}`, {
        headers: authHeaders(),
        withCredentials: true
      });
      toast.success('Entrée supprimée');
      if (editingId === postId) cancelEdit();
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div>
      <Lightbox src={lightbox?.src || ''} alt={lightbox?.alt} onClose={() => setLightbox(null)} />

      {/* Picker médiathèque */}
      {pickerTarget && (
        <div className="modal-overlay" onClick={() => setPickerTarget(null)} data-testid="media-picker">
          <div className="modal-content media-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="media-picker-head">
              <h2 className="modal-title" style={{ marginBottom: 0 }}>
                Médiathèque{pickerTarget === 'gallery' ? ' · ajout multiple' : ''}
              </h2>
              <button type="button" className="btn btn-outline" onClick={() => setPickerTarget(null)} data-testid="close-picker">
                Fermer
              </button>
            </div>
            <div className="media-picker-folders">
              {mediaFolders.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`media-folder-tab${mediaFolder === f ? ' active' : ''}`}
                  onClick={() => setMediaFolder(f)}
                >
                  {f === 'all' ? 'Tout' : f}
                </button>
              ))}
            </div>
            {mediaLoading ? (
              <div className="loading-spinner" style={{ margin: '2rem auto' }} />
            ) : (
              <div className="media-grid" data-testid="media-grid">
                {visibleMedia.map((file) => {
                  const isVideo = /\.mp4$/i.test(file.path);
                  return (
                    // Div + role="button" (pas <button>) : la loupe ci-dessous est un
                    // vrai bouton imbriqué, invalide en HTML dans un <button>.
                    <div
                      key={file.path}
                      role="button"
                      tabIndex={0}
                      className="media-tile"
                      onClick={() => pickMedia(file)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          pickMedia(file);
                        }
                      }}
                      title={file.path}
                    >
                      {isVideo ? (
                        <div className="media-tile-video">▶ vidéo</div>
                      ) : (
                        <>
                          <img src={`${BACKEND_URL}${file.url}`} alt={file.path} loading="lazy" />
                          <button
                            type="button"
                            className="tile-zoom"
                            aria-label="Agrandir"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLightbox({ src: `${BACKEND_URL}${file.url}`, alt: file.path });
                            }}
                          >
                            ⤢
                          </button>
                        </>
                      )}
                      <span className="media-tile-name">{file.path.split('/').pop()}</span>
                    </div>
                  );
                })}
                {visibleMedia.length === 0 && (
                  <p className="form-hint">Aucun média dans ce dossier.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-content">
          <a href="/" className="navbar-logo" data-testid="navbar-logo">LE REGISTRE</a>
          <div className="navbar-menu">
            <a href="/" className="navbar-link" data-testid="home-link">Journal</a>
            <div className="navbar-user">
              {user?.picture && <img src={user.picture} alt={user.name} className="user-avatar" data-testid="user-avatar" />}
              <span className="user-name" data-testid="user-name">{user?.name}</span>
              <button onClick={logout} className="btn btn-outline" data-testid="logout-button">Déconnexion</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Admin Content */}
      <div className="admin-container">
        <div className="media-picker-folders" style={{ marginBottom: '1.5rem' }}>
          <button className={`media-folder-tab${tab === 'console' ? ' active' : ''}`} onClick={() => setTab('console')} data-testid="tab-console">Console</button>
          <button className={`media-folder-tab${tab === 'studio' ? ' active' : ''}`} onClick={() => setTab('studio')} data-testid="tab-studio">Studio</button>
          <button className={`media-folder-tab${tab === 'env' ? ' active' : ''}`} onClick={() => setTab('env')} data-testid="tab-env">Environnement</button>
        </div>
        {tab === 'studio' && <StudioPanel />}
        {tab === 'env' && <EnvironmentPanel />}
        {tab === 'console' && (
        <>
        <div className="admin-header">
          <h1 className="admin-title" data-testid="admin-title">
            {editingId ? `Console · Modifier l'entrée` : 'Console · Nouvelle entrée'}
          </h1>
          {editingId && (
            <button onClick={cancelEdit} className="btn btn-outline" data-testid="cancel-edit-button">
              Annuler la modification
            </button>
          )}
        </div>

        {/* Planning : calendrier mensuel des publications */}
        <div className="cal-section" data-testid="calendar">
          <div className="cal-header">
            <h2 className="admin-title" style={{ marginBottom: 0 }}>Planning</h2>
            <div className="cal-nav">
              <button type="button" className="btn btn-outline" data-testid="cal-prev"
                onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))}>‹</button>
              <span className="cal-month-label" data-testid="cal-month">
                {calMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </span>
              <button type="button" className="btn btn-outline" data-testid="cal-next"
                onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))}>›</button>
              <button type="button" className="btn btn-secondary"
                onClick={() => { const n = new Date(); setCalMonth(new Date(n.getFullYear(), n.getMonth(), 1)); }}>
                Aujourd'hui
              </button>
            </div>
          </div>

          <div className="cal-legend">
            <span className="cal-legend-item published">Publiée</span>
            <span className="cal-legend-item scheduled">Programmée</span>
            <span className="cal-legend-item draft">Brouillon (non daté, listé dessous)</span>
          </div>

          {(() => {
            const byDay = {};
            posts.forEach((p) => {
              const iso = postPlanDate(p);
              if (!iso) return;
              const k = dayKey(new Date(iso));
              (byDay[k] = byDay[k] || []).push(p);
            });
            Object.values(byDay).forEach((list) =>
              list.sort((a, b) => new Date(postPlanDate(a)) - new Date(postPlanDate(b))));
            const todayK = dayKey(new Date());
            return (
              <div className="cal-grid">
                {DOW.map((d) => <div key={d} className="cal-dow">{d}</div>)}
                {buildCalendarCells(calMonth).map((d, i) => {
                  const k = dayKey(d);
                  const out = d.getMonth() !== calMonth.getMonth();
                  const dayPosts = byDay[k] || [];
                  return (
                    <div key={i} className={`cal-cell${out ? ' out' : ''}${k === todayK ? ' today' : ''}`}>
                      <span className="cal-day-num">{d.getDate()}</span>
                      {dayPosts.map((p) => (
                        <button
                          type="button"
                          key={p.id}
                          className={`cal-chip ${p.status || 'published'}`}
                          onClick={() => handleEdit(p)}
                          title={`${p.title}\n${STATUS_LABELS[p.status] || 'Publiée'} · ${new Date(postPlanDate(p)).toLocaleString('fr-FR')}${(p.social_platforms || []).length ? '\nRéseaux : ' + p.social_platforms.join(', ') : ''}${p.publish_site === false ? '\nRéseaux uniquement' : ''}`}
                        >
                          {new Date(postPlanDate(p)).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          {' '}{p.title}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {posts.filter((p) => p.status === 'draft').length > 0 && (
            <div className="cal-drafts" data-testid="cal-drafts">
              <span className="cal-drafts-label">À planifier :</span>
              {posts.filter((p) => p.status === 'draft').map((p) => (
                <button type="button" key={p.id} className="cal-chip draft" onClick={() => handleEdit(p)}>
                  {p.title}
                </button>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="admin-form" id="entry-form" data-testid="admin-form">
          <div className="form-group">
            <label className="form-label">Titre *</label>
            <input
              type="text"
              className="form-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              data-testid="title-input"
            />
          </div>

          {/* Index de continuité */}
          <div className="form-group form-section">
            <label className="form-label form-section-title">Index de continuité</label>
            <p className="form-hint" style={{ marginBottom: '1rem' }}>
              Métadonnées épisode (bible §11) — optionnelles pour les posts hors épisode
            </p>
            <div className="form-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Épisode</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="EP-001"
                  value={formData.episode_id}
                  onChange={(e) => setFormData({ ...formData, episode_id: e.target.value })}
                  data-testid="episode-input"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Phase</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Phase 1 · Détection"
                  value={formData.phase}
                  onChange={(e) => setFormData({ ...formData, phase: e.target.value })}
                  data-testid="phase-input"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Concept enseigné</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Signal vs bruit"
                  value={formData.concept}
                  onChange={(e) => setFormData({ ...formData, concept: e.target.value })}
                  data-testid="concept-input"
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Contenu * (chapitre / entrée)</label>
            <div className="quill-wrapper" data-testid="content-editor">
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                modules={modules}
                formats={formats}
                placeholder="Rédigez l'entrée du Registre ici…"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Média principal (image/vidéo de couverture)</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="url"
                className="form-input"
                placeholder="https://exemple.com/image.jpg"
                value={formData.media_url}
                onChange={(e) => setFormData({ ...formData, media_url: e.target.value })}
                data-testid="media-url-input"
                style={{ flex: 1 }}
              />
              <button type="button" className="btn btn-secondary" onClick={() => openPicker('media')} data-testid="pick-media-button">
                Médiathèque
              </button>
            </div>
            {formData.media_url && formData.media_type !== 'video' && (
              // Agrandissable comme les vignettes de galerie juste en dessous :
              // c'est la même sorte d'image, l'omettre était un oubli.
              <button
                type="button"
                className="img-button"
                style={{ display: 'block', marginTop: '0.75rem' }}
                onClick={() => setLightbox({ src: formData.media_url, alt: 'Média principal' })}
              >
                <img
                  src={formData.media_url}
                  alt="Aperçu du média principal"
                  style={{ maxWidth: '220px', border: '1px solid var(--line)' }}
                  data-testid="media-preview"
                />
              </button>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Type de média principal</label>
            <select
              className="form-input"
              value={formData.media_type}
              onChange={(e) => setFormData({ ...formData, media_type: e.target.value })}
              data-testid="media-type-select"
            >
              <option value="">Aucun</option>
              <option value="image">Image</option>
              <option value="video">Vidéo</option>
            </select>
          </div>

          {/* Gallery Section */}
          <div className="form-group form-section">
            <label className="form-label form-section-title">
              Galerie
            </label>
            <p className="form-hint" style={{ marginBottom: '1rem' }}>
              Ajoutez plusieurs images pour créer une galerie dans l'entrée
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <input
                type="url"
                className="form-input"
                placeholder="https://exemple.com/photo.jpg"
                value={newGalleryUrl}
                onChange={(e) => setNewGalleryUrl(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddToGallery())}
                data-testid="gallery-url-input"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={handleAddToGallery}
                className="btn btn-secondary"
                data-testid="add-to-gallery-button"
              >
                Ajouter
              </button>
              <button
                type="button"
                onClick={() => openPicker('gallery')}
                className="btn btn-secondary"
                data-testid="pick-gallery-button"
              >
                Médiathèque
              </button>
            </div>

            {formData.gallery.length > 0 && (
              <div className="gallery-preview" data-testid="gallery-preview">
                {formData.gallery.map((url, index) => (
                  <div key={index} className="gallery-item" data-testid={`gallery-item-${index}`}>
                    <button
                      type="button"
                      className="img-button"
                      onClick={() => setLightbox({ src: url, alt: `Galerie ${index + 1}` })}
                      aria-label={`Agrandir Galerie ${index + 1}`}
                    >
                      <img src={url} alt={`Galerie ${index + 1}`} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveFromGallery(index)}
                      className="gallery-remove"
                      data-testid={`remove-gallery-${index}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            <p className="form-hint" style={{ marginTop: '0.5rem' }}>
              {formData.gallery.length} image(s) dans la galerie
            </p>
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={formData.is_premium}
                onChange={(e) => setFormData({ ...formData, is_premium: e.target.checked })}
                data-testid="premium-checkbox"
              />
              <span>Archive restreinte (accès étendu requis)</span>
            </label>
          </div>

          {/* Social Media Publishing Section */}
          {(
            <div className="form-group form-section">
              <label className="form-checkbox" style={{ marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  checked={formData.publish_to_social}
                  onChange={(e) => setFormData({
                    ...formData,
                    publish_to_social: e.target.checked,
                    social_platforms: e.target.checked ? formData.social_platforms : []
                  })}
                  data-testid="publish-social-checkbox"
                />
                <span className="form-section-title">Publier sur les réseaux</span>
              </label>

              {formData.publish_to_social && (
                <div style={{ marginLeft: '2rem', marginTop: '1rem' }}>
                  <p className="form-hint" style={{ marginBottom: '0.75rem' }}>
                    Le contenu sera adapté automatiquement par IA pour chaque plateforme
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label className="form-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.social_platforms.includes('instagram')}
                        onChange={(e) => {
                          const platforms = e.target.checked
                            ? [...formData.social_platforms, 'instagram']
                            : formData.social_platforms.filter(p => p !== 'instagram');
                          setFormData({ ...formData, social_platforms: platforms });
                        }}
                        data-testid="instagram-checkbox"
                      />
                      <span>Instagram (Feed/Reels)</span>
                    </label>
                    <label className="form-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.social_platforms.includes('tiktok')}
                        onChange={(e) => {
                          const platforms = e.target.checked
                            ? [...formData.social_platforms, 'tiktok']
                            : formData.social_platforms.filter(p => p !== 'tiktok');
                          setFormData({ ...formData, social_platforms: platforms });
                        }}
                        data-testid="tiktok-checkbox"
                      />
                      <span>TikTok (Vidéo/Photo)</span>
                    </label>
                  </div>
                  {!formData.media_url && formData.publish_to_social && (
                    <p className="form-warning" style={{ marginTop: '0.75rem' }}>
                      Un média (image ou vidéo) est requis pour publier sur les réseaux sociaux
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Publication : maintenant / programmée / brouillon */}
          <div className="form-group form-section">
            <label className="form-label form-section-title">Publication</label>
            <div className="form-row" style={{ alignItems: 'flex-end' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Quand</label>
                <select
                  className="form-input"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  data-testid="status-select"
                >
                  <option value="published">Publier maintenant</option>
                  <option value="scheduled">Programmer</option>
                  <option value="draft">Garder en brouillon</option>
                </select>
              </div>
              {formData.status === 'scheduled' && (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Date et heure</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={formData.scheduled_at}
                    onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                    data-testid="scheduled-at-input"
                  />
                </div>
              )}
            </div>
            <label className="form-checkbox" style={{ marginTop: '1rem' }}>
              <input
                type="checkbox"
                checked={formData.publish_site}
                onChange={(e) => setFormData({ ...formData, publish_site: e.target.checked })}
                data-testid="publish-site-checkbox"
              />
              <span>Visible dans le journal du site (décocher = réseaux uniquement)</span>
            </label>
            {formData.status === 'scheduled' && (
              <p className="form-hint" style={{ marginTop: '0.75rem' }}>
                À l'échéance : publication sur le site (si coché) et sur les réseaux sélectionnés ci-dessus.
              </p>
            )}
          </div>

          <button type="submit" className="btn btn-primary" data-testid="submit-button">
            {editingId
              ? 'Enregistrer les modifications'
              : formData.status === 'scheduled'
                ? 'Programmer l\'entrée'
                : formData.status === 'draft'
                  ? 'Enregistrer le brouillon'
                  : 'Consigner l\'entrée'}
          </button>
        </form>

        {/* Fenêtre d'observation (hero) */}
        {hero && (
          <form onSubmit={handleSaveHero} className="admin-form" style={{ marginTop: '3rem' }} data-testid="hero-form">
            <h2 className="admin-title" style={{ marginBottom: '1.5rem' }}>Fenêtre d'observation (accueil)</h2>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Type de média</label>
                <select
                  className="form-input"
                  value={hero.media_type}
                  onChange={(e) => setHero({ ...hero, media_type: e.target.value })}
                  data-testid="hero-media-type"
                >
                  <option value="image">Image</option>
                  <option value="video">Vidéo</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label">URL du média</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="url"
                    className="form-input"
                    value={hero.media_url}
                    onChange={(e) => setHero({ ...hero, media_url: e.target.value })}
                    data-testid="hero-media-url"
                    style={{ flex: 1 }}
                  />
                  <button type="button" className="btn btn-secondary" onClick={() => openPicker('hero')} data-testid="pick-hero-button">
                    Médiathèque
                  </button>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Titre</label>
                <input
                  type="text"
                  className="form-input"
                  value={hero.title}
                  onChange={(e) => setHero({ ...hero, title: e.target.value })}
                  data-testid="hero-title"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Sous-titre</label>
                <input
                  type="text"
                  className="form-input"
                  value={hero.subtitle}
                  onChange={(e) => setHero({ ...hero, subtitle: e.target.value })}
                  data-testid="hero-subtitle"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Badge système</label>
              <input
                type="text"
                className="form-input"
                value={hero.badge_text}
                onChange={(e) => setHero({ ...hero, badge_text: e.target.value })}
                data-testid="hero-badge"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                style={{ minHeight: '80px' }}
                value={hero.description}
                onChange={(e) => setHero({ ...hero, description: e.target.value })}
                data-testid="hero-description"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={savingHero} data-testid="hero-save-button">
              {savingHero ? 'Sauvegarde…' : 'Mettre à jour la fenêtre'}
            </button>
          </form>
        )}

        {/* Posts List */}
        <div className="admin-posts-list">
          <h2 className="admin-title" style={{ marginBottom: '1rem' }}>Entrées consignées</h2>
          {posts.map((post) => (
            <div key={post.id} className="admin-post-item" data-testid={`post-item-${post.id}`}>
              <div className="admin-post-info">
                <h3 data-testid={`post-title-${post.id}`}>
                  {post.episode_id && <span className="premium-badge" style={{ marginRight: '0.5rem' }}>{post.episode_id}</span>}
                  {post.title}
                </h3>
                <p data-testid={`post-content-${post.id}`}>
                  {post.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 100)}…
                </p>
                {post.status && post.status !== 'published' && (
                  <span className="premium-badge" style={{ borderColor: 'var(--warn)', color: 'var(--warn)', background: 'var(--warn-dim)', marginRight: '0.5rem' }}>
                    {STATUS_LABELS[post.status] || post.status}
                    {post.status === 'scheduled' && post.scheduled_at && ` · ${new Date(post.scheduled_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`}
                  </span>
                )}
                {post.publish_site === false && (
                  <span className="form-hint" style={{ marginRight: '0.5rem' }}>réseaux only</span>
                )}
                {post.is_premium && <span className="premium-badge">Restreinte</span>}
                {post.concept && (
                  <span className="form-hint" style={{ marginLeft: '0.5rem' }}>{post.concept}</span>
                )}
                {post.gallery && post.gallery.length > 0 && (
                  <span className="form-hint" style={{ marginLeft: '0.5rem' }}>{post.gallery.length} photo(s)</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <button onClick={() => handleEdit(post)} className="btn btn-secondary" data-testid={`edit-button-${post.id}`}>
                  Modifier
                </button>
                <button onClick={() => handleDelete(post.id)} className="btn-danger" data-testid={`delete-button-${post.id}`}>
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
